/**
 * @file LegalGroupMembers.tsx
 * @description Документы группы по порядку (порядок = порядок на сайте;
 * phase19.16, SB141; uiux-legal-docs §4): ⋮⋮ перетаскивание (@dnd-kit, как
 * в `FooterLinksOrder`), «Выше» / «Ниже» — запасной путь для касания и
 * клавиатуры, × — убрать из группы (сам документ не удаляется).
 *
 * Неопубликованный документ остаётся в списке с пометкой «не опубликован —
 * на сайте не видно» (SB16: на сайте группа показывает только видимые).
 */

import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { LegalGroupMemberDto } from "@chaiberi/shared";
import { ArrowDown, ArrowUp, GripVertical, X } from "lucide-react";
import { Link, useParams } from "react-router";

import { Button } from "../ui/button";
import { StatusPill } from "../ui/StatusPill";
import { LEGAL_KIND_META } from "../../lib/legal/legal-doc-view";
import { cn } from "@/lib/utils";

interface Props {
  members: readonly LegalGroupMemberDto[];
  /** Новый порядок — весь список id. */
  onReorder: (ids: string[]) => void;
  onRemove: (member: LegalGroupMemberDto) => void;
  disabled?: boolean;
}

interface RowProps {
  member: LegalGroupMemberDto;
  index: number;
  count: number;
  onMove: (from: number, to: number) => void;
  onRemove: Props["onRemove"];
  disabled?: boolean;
}

function MemberRow({ member, index, count, onMove, onRemove, disabled }: RowProps) {
  const { slug } = useParams<{ slug: string }>();
  const sortable = useSortable({ id: member.id, disabled });
  return (
    <li
      ref={sortable.setNodeRef}
      style={{ transform: CSS.Transform.toString(sortable.transform), transition: sortable.transition }}
      className={cn("flex min-h-14 flex-wrap items-center gap-x-1 bg-surface-lowest", sortable.isDragging && "relative z-10 rounded-lg shadow-lg")}
    >
      <button
        type="button"
        {...sortable.attributes}
        {...sortable.listeners}
        aria-label={`Перетащить «${member.title}»`}
        className="inline-flex size-12 shrink-0 cursor-grab touch-none items-center justify-center rounded-md text-on-surface-variant outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <GripVertical aria-hidden className="size-4" />
      </button>
      {/* Основа 0 и минимум 10rem: на 360 название не рвётся по буквам — кнопки уходят второй строкой, а не прячутся. */}
      <span className="flex min-w-40 flex-[1_1_0%] flex-col py-1">
        <Link to={`/shop/${slug}/settings/legal-documents/${member.id}`} className="type-body break-words hyphens-auto text-on-surface hover:text-primary hover:underline">
          {member.title}
        </Link>
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="type-caption text-on-surface-variant">{LEGAL_KIND_META[member.kind].label}</span>
          {member.visibleOnSite ? (
            <StatusPill tone="success">Опубликован</StatusPill>
          ) : (
            <StatusPill tone="attention">не опубликован — на сайте не видно</StatusPill>
          )}
        </span>
      </span>
      <span className="ml-auto flex shrink-0 items-center">
        <Button variant="ghost" size="icon" className="size-12" aria-label={`«${member.title}» выше`} disabled={disabled || index === 0} onClick={() => onMove(index, index - 1)}>
          <ArrowUp aria-hidden className="size-4" />
        </Button>
        <Button variant="ghost" size="icon" className="size-12" aria-label={`«${member.title}» ниже`} disabled={disabled || index >= count - 1} onClick={() => onMove(index, index + 1)}>
          <ArrowDown aria-hidden className="size-4" />
        </Button>
        <Button variant="ghost" size="icon" className="size-12" aria-label={`Убрать «${member.title}» из группы`} disabled={disabled} onClick={() => onRemove(member)}>
          <X aria-hidden className="size-4" />
        </Button>
      </span>
    </li>
  );
}

export function LegalGroupMembers({ members, onReorder, onRemove, disabled }: Props) {
  const ids = members.map((m) => m.id);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const move = (from: number, to: number) => {
    if (to < 0 || to >= ids.length || from === to) return;
    onReorder(arrayMove(ids, from, to));
  };
  function onDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    move(ids.indexOf(String(active.id)), ids.indexOf(String(over.id)));
  }

  if (members.length === 0) {
    return <p className="type-body text-on-surface-variant">В группе пока нет документов. Пустая группа на сайте не видна.</p>;
  }
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <ul aria-label="Документы группы" className="flex flex-col divide-y divide-[var(--border)]">
          {members.map((member, index) => (
            <MemberRow key={member.id} member={member} index={index} count={members.length} onMove={move} onRemove={onRemove} disabled={disabled} />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}
